import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';

import { queryClient } from './lib/queryClient';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAuthStore } from './store/authStore';
import MainLayout from './layouts/MainLayout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { User } from './types';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Home = lazy(() => import('./pages/Home'));
const PlaySetup = lazy(() => import('./pages/PlaySetup'));
const PlayQuiz = lazy(() => import('./pages/PlayQuiz'));
const Results = lazy(() => import('./pages/Results'));
const GameRecap = lazy(() => import('./pages/GameRecap'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Leaderboards = lazy(() => import('./pages/Leaderboards'));
const QuizCreator = lazy(() => import('./pages/QuizCreator'));
const Friends = lazy(() => import('./pages/Friends'));
const Challenges = lazy(() => import('./pages/Challenges'));
const ChallengeDetail = lazy(() => import('./pages/ChallengeDetail'));
const FriendProfile = lazy(() => import('./pages/FriendProfile'));
const DailyChallenge = lazy(() => import('./pages/DailyChallenge'));

const FullScreenLoader: React.FC<{ text?: string }> = ({ text }) => (
  // delayed-appear: fast chunk loads never flash a spinner at all
  <div className="delayed-appear flex min-h-[60vh] items-center justify-center">
    <LoadingSpinner size="lg" text={text} />
  </div>
);

const AppContent: React.FC = () => {
  const { user, isLoading, checkAuthStatus } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" text="Loading modLRN…" />
      </div>
    );
  }

  const guarded = (element: (user: User) => React.ReactNode) =>
    user ? element(user) : <Navigate to="/login" replace />;

  return (
    <MainLayout>
      <Suspense fallback={<FullScreenLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={user && !user.is_guest ? <Navigate to="/dashboard" replace /> : <Login />} />
            <Route path="/signup" element={user && !user.is_guest ? <Navigate to="/dashboard" replace /> : <Signup />} />
            <Route path="/dashboard" element={guarded((u) => <Home user={u} />)} />
            <Route path="/play" element={guarded(() => <PlaySetup />)} />
            {/* Legacy path from the assessment era */}
            <Route path="/assessconfig" element={<Navigate to="/play" replace />} />
            <Route path="/assessment" element={guarded(() => <PlayQuiz />)} />
            <Route path="/results" element={guarded(() => <Results />)} />
            <Route path="/test-result/:resultId" element={guarded(() => <GameRecap />)} />
            <Route path="/profile" element={guarded((u) => <Profile user={u} />)} />
            <Route path="/settings" element={guarded((u) => <Settings user={u} />)} />
            <Route path="/leaderboards" element={guarded(() => <Leaderboards />)} />
            <Route path="/create" element={guarded(() => <QuizCreator />)} />
            <Route path="/friends" element={guarded(() => <Friends />)} />
            <Route path="/challenges" element={guarded(() => <Challenges />)} />
            <Route path="/challenges/:id" element={guarded(() => <ChallengeDetail />)} />
            <Route path="/daily" element={guarded(() => <DailyChallenge />)} />
            <Route path="/profile/:userId" element={guarded(() => <FriendProfile />)} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
